/**
 * Two-finger camera: pinch to zoom, move both fingers to pan — one gesture.
 *
 * Solved from where the fingers *started* rather than step by step, so a long
 * pinch cannot drift: the world point under the starting midpoint is kept under
 * the current midpoint, and the zoom is the starting zoom scaled by how far the
 * fingers now are apart compared with then. The store clamps the position to
 * the room; the zoom is clamped here, because the position has to be solved at
 * the zoom that will actually be used.
 */

import type { Camera } from '../store/viewStore'

export interface Point {
  x: number
  y: number
}

export interface PinchStart {
  camera: Camera
  a: Point
  b: Point
}

export function pinchCamera(
  start: PinchStart,
  a: Point,
  b: Point,
  limits: { minZoom: number; maxZoom: number },
): Camera {
  const startDistance = Math.hypot(start.b.x - start.a.x, start.b.y - start.a.y)
  const distance = Math.hypot(b.x - a.x, b.y - a.y)
  // Fingers that landed on one spot give no scale to compare against.
  const ratio = startDistance > 0 ? distance / startDistance : 1
  const zoom = Math.min(limits.maxZoom, Math.max(limits.minZoom, start.camera.zoom * ratio))

  const startMid = { x: (start.a.x + start.b.x) / 2, y: (start.a.y + start.b.y) / 2 }
  const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }
  const worldX = (startMid.x - start.camera.x) / start.camera.zoom
  const worldY = (startMid.y - start.camera.y) / start.camera.zoom

  return { x: mid.x - worldX * zoom, y: mid.y - worldY * zoom, zoom }
}
